import { action, computed, observable, toJS } from 'mobx';
import get from 'lodash/get';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { Explorer } from '../models/configuration/explorer/Explorer';
import { FundGroupHeader } from '../models/configuration/FundGroupHeader';
import { ExplorerWatchlist } from '../models/configuration/explorer/ExplorerWatchlist';
import { generateGuid } from '../utils/generateGuid';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { snakeCase } from 'lodash';
import { CultureSearchTransform } from '../models/configuration/CultureSearchTransform';
import { GlobalConfig } from '../models/configuration/GlobalConfig';
import { ConditionalDataConfig } from '../models/configuration/ConditionalDataConfig';
import { IncludeData } from '../models/configuration/IncludeData';
import { RatingImageOptions } from '../models/configuration/components/RatingImageOptions';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { GlobalEventFlags } from '../models/configuration/global/EventFlags';
import { SelectedModuleMode } from '../models/configuration/SelectedModuleMode';
import { OnFundTypeChangeFlags } from '../models/configuration/OnFundTypeChangeFlags';
import { getFundTypeId } from '../utils/getFundTypeId';
import { GlobalDocumentPreview } from '../models/configuration/global/DocumentPreview';
import { GlobalWatchlistConfig } from '../models/configuration/global/GlobalWatchlistConfig';
import { GlobalWatchlistConfigSource } from '../models/configuration/global/GlobalWatchlistConfigSource';
import { AppConfig } from '../models/configuration/AppConfig';

export class ConfigStore extends BaseStore {
    @observable configFile = {} as any;
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    @observable appDataProps = {};
    fundlist;

    constructor(
        stores: Stores,
        configFile,
        currentModuleId,
        currentFundType,
        fundlist,
        appDataProps
    ) {
        super(stores);
        this.configFile = this.orchestrateModuleIds(configFile);
        // define current module OR determine initial selection from module config selected flag.
        if (currentModuleId) {
            this.currentModuleId = currentModuleId;
        } else {
            const initialModule = this.modules.find(m => m.selected === true);
            if (initialModule) {
                this.currentModuleId = initialModule.id;
            }
        }
        this.currentFundType = currentFundType;
        this.fundlist = fundlist;
        this.appDataProps = appDataProps;
    }

    orchestrateModuleIds(configFile: AppConfig) {
        configFile.fundTypes.forEach(fundType => {
            fundType.modules.forEach(module => {
                module.id = `${getFundTypeId(fundType)}_${module.id}`;
            });
        });
        return configFile;
    }

    // GLOBAL Config
    // ===================

    @computed
    get globalConfig(): GlobalConfig {
        return (this.configFile && this.configFile.globalConfig) || {};
    }

    @computed
    get globalEventFlags(): GlobalEventFlags {
        return (this.globalConfig && this.globalConfig.eventFlags) || {};
    }

    // Get applicationName
    @computed
    get applicationName() {
        return this.configFile.applicationName;
    }

    @computed
    get documentPreviewEnabled(): boolean {
        const { documentPreview } = this.globalConfig;
        return (documentPreview && documentPreview.enabled) || false;
    }

    @computed
    get documentPreviewIcon(): string {
        const { documentPreview } = this.globalConfig;
        return (
            (this.documentPreviewEnabled && documentPreview.icon) ||
            'PreviewIcon'
        );
    }

    @computed
    get documentPreviewMode(): GlobalDocumentPreview['mode'] {
        const { documentPreview } = this.globalConfig;
        return (this.documentPreviewEnabled && documentPreview.mode) || 'TAB';
    }

    @computed
    get globalWatchlistConfig(): GlobalWatchlistConfig {
        const defaultConfig: GlobalWatchlistConfig = {
            source: GlobalWatchlistConfigSource.localStorage,
        };
        return {
            ...defaultConfig,
            ...(this.globalConfig.watchlist || {}),
        };
    }

    @computed
    get globalDisclaimersByDocumentType() {
        return this.globalConfig.disclaimersByDocumentType || {};
    }

    // TOP-LEVEL FUND TYPE
    // ===================

    // Sets the current module
    @action
    setFundType = fundType => {
        this.currentFundType = getFundTypeId(fundType);
    };

    // Get showFundTypes
    @computed
    get showFundTypes() {
        return this.configFile.showFundTypes || false;
    }

    // Get fund types
    @computed
    get fundTypes() {
        const { fundTypes = [] } = this.configFile;

        return fundTypes;
    }

    // Get fundTypeProperty
    @computed
    get fundTypeProperty() {
        if (
            this.selectedFundType &&
            this.selectedFundType.entity &&
            this.selectedFundType.entity.identifierPropertyForCategory
        ) {
            return this.selectedFundType.entity.identifierPropertyForCategory;
        }

        if (
            this.globalConfig &&
            this.globalConfig.entity &&
            this.globalConfig.entity.identifierPropertyForCategory
        ) {
            return this.globalConfig.entity.identifierPropertyForCategory;
        }

        return this.configFile.fundTypeProperty;
    }

    // Returns selectedFundType for either the currentFundType, the first fundType set as selected, or the first fundType
    @computed
    get selectedFundType() {
        const selectedFundType = this.fundTypes.find(f => {
            const fundTypeId = getFundTypeId(f);
            return this.currentFundType
                ? fundTypeId === this.currentFundType
                : f.selected;
        });
        const fundType = selectedFundType || this.fundTypes[0] || {};
        return fundType;
    }

    @computed
    get selectedFundTypeId(): string {
        return getFundTypeId(this.selectedFundType);
    }

    @computed
    get entityType() {
        if (this.selectedFundType.entity && this.selectedFundType.entity.type) {
            return this.selectedFundType.entity.type;
        }
        if (
            this.globalConfig &&
            this.globalConfig.entity &&
            this.globalConfig.entity.type
        ) {
            return this.globalConfig.entity.type;
        }
        return 'CLSS';
    }

    @computed
    get shareClassKey(): string {
        if (
            this.selectedFundType.entity &&
            this.selectedFundType.entity.identifierProperty
        ) {
            return this.selectedFundType.entity.identifierProperty;
        }
        if (this.globalConfig) {
            if (
                this.globalConfig.entity &&
                this.globalConfig.entity.identifierProperty
            ) {
                return this.globalConfig.entity.identifierProperty;
            }
            // TODO: deprecate and only rely on entity.identifierProperty
            if (this.globalConfig.shareClassKey) {
                return this.globalConfig.shareClassKey;
            }
        }
        return 'isin';
    }

    @computed
    get disabledEntityFlag(): ConditionalDataConfig | undefined {
        if (
            this.selectedFundType.entity &&
            this.selectedFundType.entity.isDisabled
        ) {
            return this.selectedFundType.entity.isDisabled;
        }
        if (this.globalConfig) {
            if (
                this.globalConfig.entity &&
                this.globalConfig.entity.isDisabled
            ) {
                return this.globalConfig.entity.isDisabled;
            }
            // TODO: deprecate and only rely on entity.isDisabled
            if (this.globalConfig.disabledEntityFlag) {
                return this.globalConfig.disabledEntityFlag;
            }
        }
        return;
    }

    // MODULES FOR SELECTED FUND TYPE
    // ==============================

    // Sets the current module
    @action
    setModule = module => {
        this.currentModuleId = module.id;
    };

    // this method updates the currently selected module based on the initially selected module, if the settings condone it.
    @action
    setDefaultModule = () => {
        if (this.selectedModuleMode === 'SwitchToDefault') {
            const firstModule = this.modules[0];
            const defaultModule = this.modules.find(
                module => module.selected === true
            );
            this.currentModuleId =
                (defaultModule && defaultModule.id) ||
                (firstModule && firstModule.id);
        }
    };

    // Get showFundTypes
    @computed
    get showModules() {
        return this.selectedFundType.showModules || false;
    }

    // Get showFundTypes
    @computed
    get moduleMobileSelector() {
        return this.selectedFundType.moduleMobileSelector || 'tabs';
    }

    // Returns available modules from selectedFundType
    @computed
    get modules() {
        const { modules = [] } = this.selectedFundType;
        return modules;
    }

    @computed
    get globalFundTypeChangeFlags(): OnFundTypeChangeFlags {
        return (
            (this.globalEventFlags && this.globalEventFlags.onFundTypeChange) ||
            {}
        );
    }

    @computed
    get fundTypeChangeFlags(): OnFundTypeChangeFlags {
        return (
            (this.selectedFundType && this.selectedFundType.onChangeFlags) || {}
        );
    }

    @computed
    get clearFiltersOnChange(): boolean {
        // change flags overrides old individual flags
        const { clearFilters } = this.fundTypeChangeFlags;
        if (!isNullOrUndefined(clearFilters)) {
            return clearFilters;
        }
        const { clearFiltersOnChange } = this.selectedFundType;
        if (!isNullOrUndefined(clearFiltersOnChange)) {
            return clearFiltersOnChange;
        }
        // change flags overrides old individual flags for global
        const {
            clearFilters: globalClearFilters,
        } = this.globalFundTypeChangeFlags;
        if (!isNullOrUndefined(globalClearFilters)) {
            return globalClearFilters;
        }
        const { clearFiltersOnFundTypeChange } = this.globalConfig;
        if (!isNullOrUndefined(clearFiltersOnFundTypeChange)) {
            return clearFiltersOnFundTypeChange;
        }
        return true;
    }

    @computed
    get selectedModuleMode(): SelectedModuleMode {
        const { selectedModuleMode } = this.fundTypeChangeFlags;
        if (!isNullOrUndefined(selectedModuleMode)) {
            return selectedModuleMode;
        }

        const {
            selectedModuleMode: globalSelectedModuleMode,
        } = this.globalFundTypeChangeFlags;
        if (!isNullOrUndefined(globalSelectedModuleMode)) {
            return globalSelectedModuleMode;
        }

        return 'MaintainSelection';
    }

    // Returns explorer related configurations
    @computed
    get explorer(): Explorer {
        return this.selectedModuleConfig.explorer || {};
    }

    // This method should return the currently selected module or the first one if not defined. (initial selection should be handled in the constructor)
    @computed
    get selectedModule() {
        const firstModule = this.modules[0];
        const selectedModule = this.modules.find(
            m => m.id === this.currentModuleId
        );
        return selectedModule || firstModule || {};
    }

    @computed
    get isDocumentModule() {
        const { type } = this.selectedModule;
        return type === 'DocumentList';
    }

    // TODO: Rename config => selectedModule Config
    // CONFIG FOR SELECTED MODULE
    // ==========================

    // Returns config for the selected module
    @computed
    get selectedModuleConfig() {
        const { config = {} } = this.selectedModule;

        return config;
    }

    // VALUES DERIVED FROM CONFIG FOR SELECTED MODULE
    // ==============================================

    @computed
    get noDataText(): string {
        return (
            this.selectedModuleConfig.noDataText ||
            this.selectedFundType.noDataText ||
            'There are no funds matching this search'
        );
    }

    @computed
    get stickyOffset() {
        const { stickyOffset } = this.fundGroupHeader;
        return stickyOffset || this.selectedModuleConfig.stickyOffset || 0;
    }

    @computed
    get defaultCulture() {
        return (
            this.selectedModuleConfig.defaultCulture ||
            this.globalConfig.defaultCulture ||
            'en-GB'
        );
    }

    @computed
    get sourceCulture() {
        return (
            (this.globalConfig && this.globalConfig.sourceCulture) || 'Default'
        );
    }

    @computed
    get useHtmlTranslations() {
        return (
            (this.globalConfig && this.globalConfig.useHtmlTranslations) ||
            false
        );
    }

    @computed
    get limit() {
        return this.selectedModuleConfig.searchLimit || 30;
    }

    @computed
    get sort() {
        const configSort = toJS(this.selectedModuleConfig.sort) || [];
        // replace "app:" place holders with app data props
        const parsedSort = configSort.map(item => {
            item.key = snakeCase(
                replacePlaceholders({
                    text: item.key,
                    appParams: this.appDataProps,
                })
            );
            return item;
        });
        return observable(parsedSort);
    }

    @computed
    get advancedSort() {
        return this.selectedModuleConfig.advancedSort;
    }

    @computed
    get disclaimersByDocumentType() {
        return this.selectedModuleConfig.disclaimersByDocumentType || {};
    }

    @computed
    get watchListProperty() {
        const property =
            (this.watchListConfig && this.watchListConfig.property) ||
            this.selectedModuleConfig.watchListProperty;
        return property;
    }

    @computed
    get watchListEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.selectedModuleConfig.watchListEnabled;
        return enabled;
    }

    @computed
    get watchListConfig(): ExplorerWatchlist {
        return (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.explorer &&
            this.selectedModuleConfig.explorer.watchlist
        );
    }

    @computed
    get responsiveConfig(): any {
        return (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.responsiveConfig
        );
    }

    @computed
    get fundGroupHeader(): FundGroupHeader {
        const fundGroupHeader = this.selectedModuleConfig.fundGroupHeader || {};
        const fundGroupProperty = this.selectedModuleConfig.fundGroupProperty;
        const stickyOffset = this.selectedModuleConfig.stickyOffset || 0;
        return {
            fundGroupProperty,
            stickyOffset,
            ...fundGroupHeader,
        };
    }

    @computed
    get fundGroupProperty() {
        const { fundGroupProperty } = this.fundGroupHeader;
        return fundGroupProperty || this.selectedModuleConfig.fundGroupProperty;
    }

    @computed
    get fundSubGroupProperty() {
        const { fundSubGroupProperty } = this.fundGroupHeader;
        return (
            fundSubGroupProperty ||
            this.selectedModuleConfig.fundSubGroupProperty
        );
    }

    @computed
    get nonGroupHeaderText() {
        const { nonGroupHeaderText } = this.fundGroupHeader;
        return (
            nonGroupHeaderText || this.selectedModuleConfig.nonGroupHeaderText
        );
    }

    @computed
    get fundCurrencyProperty() {
        return this.selectedModuleConfig.fundCurrencyProperty;
    }

    @computed
    get identifierSearch() {
        return this.selectedModuleConfig.identifierSearch || {};
    }

    @computed
    get documentsJoinSpec() {
        return this.selectedModuleConfig.documentsJoinSpec || [];
    }

    @computed
    get timeseriesInclude() {
        const { timeseriesInclude } = this.selectedModuleConfig;
        if (this.isExcluded(timeseriesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return timeseriesInclude || {};
    }

    @computed
    get allocationsInclude() {
        const { allocationsInclude } = this.selectedModuleConfig;
        if (this.isExcluded(allocationsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return allocationsInclude || {};
    }

    @computed
    get statisticsInclude() {
        const { statisticsInclude } = this.selectedModuleConfig;
        if (this.isExcluded(statisticsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return statisticsInclude || {};
    }

    @computed
    get commentariesInclude() {
        const { commentariesInclude } = this.selectedModuleConfig;
        if (this.isExcluded(commentariesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return commentariesInclude || {};
    }

    @computed
    get disclaimersInclude() {
        const { disclaimersInclude } = this.selectedModuleConfig;
        if (this.isExcluded(disclaimersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return disclaimersInclude || {};
    }

    @computed
    get fundManagersInclude() {
        const { fundManagersInclude } = this.selectedModuleConfig;
        if (this.isExcluded(fundManagersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return fundManagersInclude || {};
    }

    @computed
    get includeProperties() {
        const { includeProperties } = this.selectedModuleConfig;
        return includeProperties;
    }

    isExcluded(includeData: IncludeData) {
        return includeData && includeData.exclude;
    }

    @computed
    get filterOptions() {
        if (this.selectedModuleConfig.filterConfig) {
            return this.selectedModuleConfig.filterConfig;
        }

        const filtersTopExpanded = this.selectedModuleConfig.hasOwnProperty(
            'filtersTopExpanded'
        )
            ? this.selectedModuleConfig.filtersTopExpanded
            : true;

        const filtersTopExpandable = this.selectedModuleConfig.hasOwnProperty(
            'filtersTopExpandable'
        )
            ? this.selectedModuleConfig.filtersTopExpandable
            : true;

        return {
            filtersTopExpanded,
            filtersTopExpandable,
            top: this.selectedModuleConfig.filtersTop,
            clearIndividualFilters: this.selectedModuleConfig
                .clearIndividualFilters,
            shareableLink:
                this.selectedModuleConfig &&
                this.selectedModuleConfig.filterConfig &&
                this.selectedModuleConfig.filterConfig.shareableLink,
            filters: this.selectedModuleConfig.filters,
            minimumFiltersNeeded:
                this.selectedModuleConfig.hasOwnProperty(
                    'minimumFiltersNeeded'
                ) && this.selectedModuleConfig.minimumFiltersNeeded,
        };
    }

    @computed
    get filtersConfig() {
        return this.filterOptions.filters || [];
    }

    @computed
    get filtersConfigByProperty() {
        return (
            (this.filtersConfig &&
                this.filtersConfig.reduce((config, filterConfig) => {
                    if (!config[filterConfig.property]) {
                        config[filterConfig.property] = filterConfig;
                    }
                    return config;
                }, {})) ||
            {}
        );
    }

    isDocumentFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return (
            filterConfig &&
            ['DOCUMENT_META', 'DOCUMENT_PROPERTY'].indexOf(filterConfig.type) >=
                0
        );
    }

    isDocumentPropertyFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return filterConfig && filterConfig.type === 'DOCUMENT_PROPERTY';
    }

    isDocumentMetaFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return filterConfig && filterConfig.type === 'DOCUMENT_META';
    }

    @computed
    get clearIndividualFilters() {
        return this.filterOptions.clearIndividualFilters;
    }

    @computed
    get filtersTop() {
        return this.filterOptions.top;
    }

    @computed
    get clearFiltersLabel() {
        return this.filterOptions.clearFiltersLabel;
    }

    @computed
    get shareableLink() {
        return this.filterOptions.shareableLink;
    }

    @computed
    get filtersTopExpanded() {
        return this.filterOptions.hasOwnProperty('topExpanded')
            ? this.filterOptions.topExpanded
            : true;
    }

    @computed
    get filtersTopExpandable() {
        return this.filterOptions.hasOwnProperty('topExpandable')
            ? this.filterOptions.topExpandable
            : true;
    }

    @computed
    get documentSearch() {
        return this.selectedModuleConfig.documentSearch || [];
    }

    @computed
    get globalDocumentSearch() {
        return this.selectedModuleConfig.globalDocumentSearch || [];
    }

    @computed
    get documentSearchBySegmentation() {
        return get(
            this.selectedModuleConfig.documentSearchBySegmentation,
            this.fundlist,
            []
        );
    }

    @computed
    get documentSegmentationFilters() {
        return get(
            this.selectedModuleConfig.documentSegmentationFilters,
            this.fundlist,
            {}
        );
    }

    @computed
    get documentGroupMeta() {
        return this.selectedModuleConfig.documentGroupMeta || '';
    }

    @computed
    get documentGroupOrder() {
        return this.selectedModuleConfig.documentGroupOrder || [];
    }

    @computed
    get documentGroupOrderMetaPropertySort() {
        return (
            this.selectedModuleConfig.documentGroupOrderMetaPropertySort || {}
        );
    }

    @computed
    get documentUngroupedLabel() {
        return this.selectedModuleConfig.documentUngroupedLabel || '';
    }

    @computed
    get noDataPlaceholder() {
        return this.selectedModuleConfig.noDataPlaceholder || '–';
    }

    @computed
    get cultureAliases() {
        const globalCultureAliases =
            this.globalConfig &&
            this.globalConfig.valueAliases &&
            this.globalConfig.valueAliases.cultures;
        return (
            {
                ...globalCultureAliases,
                ...this.selectedModuleConfig.cultureAliases,
            } || {}
        );
    }

    @computed
    get selectedDocumentsLabel() {
        return (
            this.selectedModuleConfig.selectedDocumentsLabel || {
                singular: `{selected}/{total} document selected`,
                plural: `{selected}/{total} documents selected`,
            }
        );
    }

    @computed
    get availableDocumentsLabel() {
        return (
            this.selectedModuleConfig.availableDocumentsLabel || {
                singular: `{total} document available`,
                plural: `{total} documents available`,
            }
        );
    }

    @computed
    get columnConfig() {
        const columnConfig = this.selectedModuleConfig.columnConfig || [];
        // Create unique key for link filtering
        for (const column of columnConfig) {
            column.key = column.key || generateGuid();
        }
        return columnConfig;
    }

    @computed
    get headerDisclaimerTypes() {
        return this.selectedModuleConfig.headerDisclaimerTypes || [];
    }

    // If present, config.footerDisclaimerTypes takes precedence over config.disclaimerTypes
    // config.disclaimerTypes should be considered a legacy option
    @computed
    get footerDisclaimerTypes() {
        return (
            this.selectedModuleConfig.footerDisclaimerTypes ||
            this.selectedModuleConfig.disclaimerTypes ||
            []
        );
    }

    @computed
    get rowConfig() {
        const rowConfig = this.selectedModuleConfig.rowConfig;
        // Create unique key for link filtering.
        if (rowConfig && rowConfig.header) {
            rowConfig.header.key = rowConfig.header.key || generateGuid();
        }

        return rowConfig || {};
    }

    @computed
    get performanceCheck() {
        return this.selectedModuleConfig.performanceCheck || {};
    }

    @computed
    get segmentationFilter() {
        return this.selectedModuleConfig.segmentationFilter || {};
    }

    @computed
    get documentFilename() {
        return this.selectedModuleConfig.documentFilename || '';
    }

    @computed
    get documentDisplayName() {
        return this.selectedModuleConfig.documentDisplayName;
    }

    @computed
    get searchMillisecondTimeout(): number {
        if (this.globalConfig && this.globalConfig.searchMillisecondTimeout) {
            return this.globalConfig.searchMillisecondTimeout;
        }
        return 600;
    }

    @computed
    get autoSearchMinChar(): number {
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.search &&
            this.selectedModuleConfig.search.config &&
            this.selectedModuleConfig.search.config.autoSearchMinChar
        ) {
            return this.selectedModuleConfig.search.config.autoSearchMinChar;
        }
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.autoSearchMinChar
        ) {
            return this.selectedModuleConfig.autoSearchMinChar;
        }
        if (this.globalConfig && this.globalConfig.autoSearchMinChar) {
            return this.globalConfig.autoSearchMinChar;
        }

        return 1;
    }

    @computed
    get cultureSearchTransform(): CultureSearchTransform | undefined {
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.cultureSearchTransform
        ) {
            return this.selectedModuleConfig.cultureSearchTransform;
        }
        if (this.globalConfig && this.globalConfig.cultureSearchTransform) {
            return this.globalConfig.cultureSearchTransform;
        }
        return;
    }

    @computed
    get ratingImageOptions(): RatingImageOptions {
        return this.selectedModuleConfig.rowConfig.ratingImageOptions;
    }
}



// WEBPACK FOOTER //
// ./src/stores/ConfigStore.ts